import React, { useEffect, useRef } from 'react';

/**
 *
 * @param value: it is for taking the user default input and adjust height as per that.
 * @param maxHeight: it is for setting the maximum height of the input field.
 * @param props: it is for taking the other props.
 *
 * @description
 * CustomInput is a React functional component that renders a textarea input field.
 * It automatically adjusts its height based on the content inside it, up to a specified maximum height.
 * The component uses the useRef and useEffect hooks to manage the textarea's height dynamically.
 * @returns
 */

const CustomInput = ({ value, maxHeight = 150, ...props }) => {
  const textareaRef = useRef(null);

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto'; // reset height
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;

    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  // Adjust height on mount + when `value` changes (for controlled components)
  useEffect(() => {
    adjustHeight();
  }, [value]);

  // Also run once on mount (covers defaultValue + uncontrolled)
  useEffect(() => {
    adjustHeight();
  }, []);

  return (
    <textarea
      {...props}
      value={value}
      ref={textareaRef}
      rows={1}
      onInput={adjustHeight}
      style={{
        resize: 'none',
        overflowY: 'hidden',
        minHeight: '32px',
        height: '32px',
        maxHeight: `${maxHeight}px`,
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: '4px',
        transition: 'height 0.2s ease',
      }}
    />
  );
};

export default CustomInput;
